import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Card } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faTrash, faFilter, faSync, faBuildingColumns } from '@fortawesome/free-solid-svg-icons';
import RequireRoles from '../../../../components/RequireRoles';
import PaginatedItems from '../../../../components/administracao/Pagination';
import useFetch from '../../../../hooks/useFetch';
import Swal from 'sweetalert2';
import { OPCOES_TIPO_ACERVO } from './AcervoDetentor';

export default function GerenciarAcervos() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        acervos: [],
        count: 0,
        filtros: { nome: "", tipo: "" },
        filtroAtivo: false,
        mostrarModal: false,
        registroAExcluir: { nome: "" },
        loading: true
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [acervos, count] = await buscarAcervos({});
            setState(s => ({ ...s, acervos, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarAcervos(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") query = query.concat(key, "=", filtros[key], "&");
            }
        }
        try {
            let response = await get('/acervodetentor' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarAcervos():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [acervos, count] = await buscarAcervos(filtros);
        setState({ ...state, acervos, count, loading: false });
    }

    const filtrar = async () => {
        let [acervos, count] = await buscarAcervos(state.filtros);
        setState(s => ({ ...s, acervos, count, filtroAtivo: true, loading: false }));
    };

    const limpar = async () => {
        let [acervos, count] = await buscarAcervos({});
        setState(s => ({ ...s, filtros: defaultState.filtros, acervos, count, filtroAtivo: false, loading: false }));
    };

    async function excluirAcervo() {
        try {
            let response = await del('/acervodetentor/' + state.registroAExcluir.id_acervo_detentor);
            if (response.status === 200) {
                let [acervos, count] = await buscarAcervos(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, acervos, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "Acervo em uso por alguma fotografia.", icon: "error", confirmButtonColor: "#587b7f" });
            } else {
                console.error('Erro no excluirAcervo()');
                Swal.fire({ title: "Não foi possível excluir o acervo", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirAcervo():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Acervos</h2>
                    <p className="admin-subtitle">Acervos onde as fotografias estão guardadas — departamentos, institutos ou pessoas responsáveis pela guarda</p>
                </div>
                <Link to="/admin/iconografico/acervos/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Acervo
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={(e) => { e.preventDefault(); filtrar(); }}>
                        <div className="row g-3">
                            <Form.Group className="col-md-8">
                                <Form.Label className="label-admin">Nome do Acervo</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Departamento de Bioquímica..."
                                    value={state.filtros.nome}
                                    onChange={e => setState({ ...state, filtros: { ...state.filtros, nome: e.target.value } })}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-4">
                                <Form.Label className="label-admin">Tipo</Form.Label>
                                <Form.Select
                                    className="input-moderno"
                                    value={state.filtros.tipo}
                                    onChange={e => setState({ ...state, filtros: { ...state.filtros, tipo: e.target.value } })}
                                >
                                    <option value="">Todos</option>
                                    {OPCOES_TIPO_ACERVO.map((v) => <option key={v} value={v}>{v}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="filter-actions-row mt-3">
                            <div className="d-flex gap-2">
                                <Button type="button" className="btn-apply-filters" onClick={filtrar}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                                </Button>
                                <Button type="button" className="btn-clear-filters" onClick={limpar}>
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th>Status</th>
                                <th>Observações</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.acervos.length > 0 ? (
                                state.acervos.map((acervo) => (
                                    <tr key={acervo.id_acervo_detentor}>
                                        <td data-label="Nome" className="fw-bold">
                                            <FontAwesomeIcon icon={faBuildingColumns} className="me-2 text-muted small" />
                                            {acervo.nome}
                                        </td>
                                        <td data-label="Tipo">{acervo.tipo || '-'}</td>
                                        <td data-label="Status">{acervo.status_acervo_detentor === 'inativo' ? 'Inativo' : 'Ativo'}</td>
                                        <td data-label="Observações" className="text-truncate" style={{ maxWidth: '320px' }}>{acervo.observacoes || '-'}</td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/iconografico/acervos/${acervo.id_acervo_detentor}`} state={{ acao: "Visualizar" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/iconografico/acervos/${acervo.id_acervo_detentor}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: acervo })}><FontAwesomeIcon icon={faTrash} /></button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-4">Nenhum acervo encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.acervos.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Acervo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir o acervo <strong>{state.registroAExcluir.nome}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" className="btn-modal-cancel" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirAcervo}>Confirmar Exclusão</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
